import type {
  ColDef,
  ICellRendererParams,
  CellClassParams,
  CellStyle,
} from 'ag-grid-community';
import { useCallback, useMemo } from 'react';

import { Markdown } from 'components/Markdown';
import { AgTable } from 'components/table/AgTable';

import { getAdaptiveMinWidth } from 'utils/maxi-ai/tableLayout';

import type { TableData } from 'types/maxi-ai';

interface TableComponentProps {
  tableData: TableData;
}

const TABLE_CELL_PADDING = '8px 12px';

// Clean and normalize value for numeric operations
// Handles: numbers, comma-separated numbers (e.g., "20,000"), currency symbols, percentages, accounting negatives
function cleanNumericValue(value: unknown): string {
  if (typeof value === 'number') return String(value);
  if (!value) return '';

  const str = String(value).trim();
  if (!str) return '';

  // Handle accounting negatives: (123) -> -123
  const isAccountingNegative = str.startsWith('(') && str.endsWith(')');
  const cleanStr = isAccountingNegative
    ? '-' +
      str
        .slice(1, -1)
        .trim()
        .replace(/[$,\s%]/g, '')
    : str.replace(/[$,\s%]/g, '');

  return cleanStr;
}

// Check if value represents a number (handles comma-separated numbers like "20,000")
function isNumericString(value: unknown): boolean {
  if (typeof value === 'number') return isFinite(value);
  const cleaned = cleanNumericValue(value);
  return cleaned !== '' && !isNaN(Number(cleaned)) && isFinite(Number(cleaned));
}

// Parse value as number for sorting (handles comma-separated numbers like "20,000")
function parseNumericValue(value: unknown): number {
  if (typeof value === 'number') return isFinite(value) ? value : NaN;
  const cleaned = cleanNumericValue(value);
  const num = parseFloat(cleaned);
  return isNaN(num) ? NaN : num;
}

// Detect if column contains primarily numeric data
// Checks if values can be parsed as numbers (handles commas, currency, etc.)
function isNumericColumn(columnData: unknown[]): boolean {
  const validValues = columnData.filter(
    (val) => val != null && String(val).trim()
  );
  if (validValues.length === 0) return false;

  const numericCount = validValues.filter(isNumericString).length;
  // Require at least 80% of non-empty values to be numeric
  return numericCount / validValues.length >= 0.8;
}

export function TableComponent({ tableData }: TableComponentProps) {
  // Cell renderer with markdown support
  const cellRenderer = useCallback((params: ICellRendererParams) => {
    if (!params.value) return '';

    return (
      <Markdown
        text={params.value.toString()}
        className="[&_ol]:pl-3 [&_ul]:pl-3 [&_li]:ml-3 [&_strong]:font-medium [&_strong]:text-color-primary leading-5"
      />
    );
  }, []);

  // Analyze columns to determine which are numeric
  const columnTypes = useMemo(() => {
    if (!tableData.rows?.length || !tableData.headers?.length) return [];

    return tableData.headers.map((_, index) => {
      const columnData = tableData.rows.map(
        (row) => row.cells?.[index]?.content
      );
      return isNumericColumn(columnData);
    });
  }, [tableData.rows, tableData.headers]);

  // Generate column definitions
  const columnDefs: ColDef[] = useMemo(() => {
    if (!tableData.headers?.length) return [];

    const columnCount = tableData.headers.length;

    return tableData.headers.map((header, index) => {
      const columnSize = header.column_size || 'medium';
      const isNumeric = columnTypes[index];

      // Use flex weights for responsive sizing
      const flex =
        columnSize === 'small' ? 0.7 : columnSize === 'large' ? 1.5 : 1;

      const colDef: ColDef = {
        field: `col_${index}`,
        headerName: header.content,
        flex,
        minWidth: getAdaptiveMinWidth(columnSize, columnCount),
        cellRenderer,
        cellStyle: (params: CellClassParams) => {
          const isHighlighted = params.data?.[`col_${index}_highlighted`];
          const style: CellStyle = {
            wordBreak: 'break-word',
            padding: TABLE_CELL_PADDING,
          };

          if (isHighlighted) {
            style.backgroundColor = '#fef3c7';
          }

          return style;
        },
      };

      // Add custom comparator for numeric columns to enable proper sorting
      if (isNumeric) {
        colDef.comparator = (valueA: unknown, valueB: unknown) => {
          const numA = parseNumericValue(valueA);
          const numB = parseNumericValue(valueB);

          // Numeric comparison if both are valid finite numbers, otherwise string comparison
          return Number.isFinite(numA) && Number.isFinite(numB)
            ? numA - numB
            : String(valueA ?? '').localeCompare(String(valueB ?? ''));
        };

        // Add column type for right alignment and better AG-Grid integration
        colDef.type = 'rightAligned';
      }

      return colDef;
    });
  }, [tableData.headers, cellRenderer, columnTypes]);

  // Transform table rows into AG-Grid row data format
  const rowData = useMemo(() => {
    if (!tableData.rows?.length) return [];

    return tableData.rows.map((row) => {
      const rowObj: Record<string, string | boolean> = {};

      row.cells?.forEach((cell, index) => {
        rowObj[`col_${index}`] = cell?.content || '';
        rowObj[`col_${index}_highlighted`] = cell?.highlight_cell || false;
      });

      return rowObj;
    });
  }, [tableData.rows]);

  const columnCount = tableData.headers?.length || 0;
  const allowHorizontalScroll = columnCount > 7;

  return (
    <div className="my-4 overflow-x-auto">
      <div className="w-full min-w-fit">
        <AgTable
          columnDefs={columnDefs}
          rowData={rowData}
          pagination={false}
          domLayout="autoHeight"
          suppressMovableColumns={true}
          defaultColDef={{
            wrapText: true,
            autoHeight: true,
            sortable: true,
          }}
          animateRows={false}
          enableCellTextSelection={true}
          suppressHorizontalScroll={!allowHorizontalScroll}
          showZoomControls={false}
          className="h-auto w-full"
          noRowTitle="No data available"
          noRowDescription=""
        />
      </div>
    </div>
  );
}
